import React from "react";
import { motion } from "framer-motion";
import {
  Link2,
  Users,
  Stethoscope,
  FlaskConical,
  Pill,
  FileText,
  CalendarCheck,
  Mic2,
  CreditCard,
  QrCode,
  ShieldCheck,
  ArrowRight,
  Check,
  Network,
  Building2,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";
import { Link } from "react-router-dom";


const Integrations = () => {

  
  const integrations = [
    {
      icon: Users,
      title: "Patient Care",
      description:
        "Connect patient-facing services such as care discovery, appointments, virtual consultations and health records.",
    },
    {
      icon: Stethoscope,
      title: "Clinical Workflows",
      description:
        "Connect healthcare professionals with EHR, scheduling, e-prescriptions, billing and consultation workflows.",
    },
    {
      icon: FlaskConical,
      title: "Diagnostics",
      description:
        "Connect supported laboratory and diagnostic services with patient care and relevant health information.",
    },
    {
      icon: Pill,
      title: "Pharmacy",
      description:
        "Connect digital prescriptions with supported pharmacy and medication workflows.",
    },
    {
      icon: FileText,
      title: "Health Records",
      description:
        "Keep relevant consultations, reports, prescriptions and healthcare information connected.",
    },
    {
      icon: QrCode,
      title: "eMC Card",
      description:
        "Connect a patient's medical information through the eMC Card and its QR-based access workflow.",
    },
  ];

  const workflow = [
    {
      number: "01",
      icon: Users,
      title: "Patient",
      description: "Starts the healthcare journey.",
    },
    {
      number: "02",
      icon: CalendarCheck,
      title: "Appointment",
      description: "Books or schedules care.",
    },
    {
      number: "03",
      icon: Stethoscope,
      title: "Consultation",
      description: "Connects with a healthcare professional.",
    },
    {
      number: "04",
      icon: FlaskConical,
      title: "Diagnostics",
      description: "Completes supported tests and receives reports.",
    },
    {
      number: "05",
      icon: Pill,
      title: "Prescription",
      description: "Connects prescriptions with medication workflows.",
    },
  ];

  const capabilities = [
    "EHR and digital health records",
    "Appointment and scheduling workflows",
    "Virtual consultations",
    "e-Prescriptions",
    "Diagnostic and laboratory workflows",
    "Pharmacy workflows",
    "Consultation voice-to-text",
    "Consultation summaries",
    "eMC Card and QR-based access",
    "Billing workflows",
  ];

  return (
    <>
      <Navbar />
      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
          HERO
      ====================================================== */}
        <section className="relative min-h-[680px] bg-gradient-to-br from-cyan-50 via-white to-blue-50 overflow-hidden">

          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-300/20 blur-3xl" />

          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-300/20 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
              backgroundSize: "45px 45px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">

            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/80 px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">
                  Connected Healthcare
                </span>
              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[58px] font-medium tracking-[-0.05em] leading-[1.05] mt-7">
                Connect Every Part
                <span className="block text-teal-500 mt-2">
                  of Healthcare.
                </span>
              </h1>

              <p className="text-slate-600 text-lg leading-8 mt-8 max-w-xl">
                Doxzon connects patients, healthcare professionals, diagnostics,
                pharmacy services and health information into one connected
                healthcare experience.
              </p>
              {/* 
            <div className="flex flex-wrap gap-4 mt-9">
              <button className="inline-flex items-center gap-2 rounded-full bg-slate-950 text-white px-7 py-4 font-semibold">
                Explore Integrations
                <ArrowRight size={17} />
              </button>

              <button className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-7 py-4 font-semibold text-slate-700">
                View Platform
              </button>
            </div> */}

              <div className="flex flex-wrap gap-6 mt-8 text-sm text-slate-500">
                <span className="flex items-center gap-2">
                  <ShieldCheck size={17} className="text-teal-500" />
                  Connected workflows
                </span>

                <span className="flex items-center gap-2">
                  <Check size={17} className="text-teal-500" />
                  Healthcare focused
                </span>
              </div>
            </motion.div>

            {/* RIGHT VISUAL */}
            <motion.div
              initial={{ opacity: 0, x: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="relative"
            >
              <div className="absolute inset-[-35px] rounded-[40px] border border-teal-200/50" />

              <div className="relative bg-white rounded-[30px] border-8 border-white shadow-[0_25px_70px_rgba(15,23,42,0.15)] p-7">

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-slate-400">
                      Doxzon Platform
                    </p>

                    <h3 className="text-xl font-bold mt-1">
                      Connected Healthcare
                    </h3>
                  </div>

                  <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center">
                    <Network className="text-teal-600" size={21} />
                  </div>
                </div>

                {/* CENTER */}
                <div className="relative flex items-center justify-center py-14">

                  <div className="absolute w-56 h-56 rounded-full border border-teal-100" />

                  <div className="absolute w-40 h-40 rounded-full border border-cyan-100" />

                  <div className="relative z-10 w-24 h-24 rounded-3xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white grid place-items-center shadow-xl">
                    <Link2 size={34} />
                  </div>

                  {/* nodes */}
                  <div className="absolute top-1 left-1/2 -translate-x-1/2 w-12 h-12 rounded-xl bg-white shadow-md border grid place-items-center">
                    <Users size={20} className="text-teal-600" />
                  </div>

                  <div className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-xl bg-white shadow-md border grid place-items-center">
                    <FlaskConical size={20} className="text-teal-600" />
                  </div>

                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-12 h-12 rounded-xl bg-white shadow-md border grid place-items-center">
                    <Pill size={20} className="text-teal-600" />
                  </div>

                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-xl bg-white shadow-md border grid place-items-center">
                    <Stethoscope size={20} className="text-teal-600" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    ["Patient Care", Users],
                    ["Clinical", Stethoscope],
                    ["Diagnostics", FlaskConical],
                    ["Pharmacy", Pill],
                  ].map(([label, Icon]) => (
                    <div
                      key={label}
                      className="flex items-center gap-3 bg-slate-50 rounded-xl p-3"
                    >
                      <Icon size={17} className="text-teal-600" />
                      <span className="text-xs font-semibold">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* =====================================================
          INTRO
      ====================================================== */}
        <section className="py-28">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">

            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                Why Integrations Matter
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
                Healthcare doesn't happen
                <span className="text-teal-500"> in one place.</span>
              </h2>
            </div>

            <div>
              <p className="text-lg text-slate-600 leading-8">
                A patient's healthcare journey can involve appointments,
                consultations, diagnostic tests, prescriptions, pharmacy
                services and follow-up care.
              </p>

              <p className="text-lg text-slate-600 leading-8 mt-5">
                Doxzon is designed to connect these workflows so relevant
                information can move through the healthcare journey more
                efficiently.
              </p>
            </div>

          </div>
        </section>

        {/* =====================================================
          INTEGRATION AREAS
      ====================================================== */}
        <section className="bg-[#f6fbfc] py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                Integration Areas
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
                One platform.
                <span className="text-teal-500">
                  {" "}Connected workflows.
                </span>
              </h2>
            </div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10"
            >
              {integrations.map((item, index) => {
                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    variants={{
                      hidden: { opacity: 0, y: 25 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.6,
                          delay: index * 0.08,
                        },
                      },
                    }}
                    whileHover={{ y: -8 }}
                    className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-xl transition"
                  >
                    <div className="w-10 h-10 rounded-xl bg-teal-50 grid place-items-center">
                      <Icon size={18} className="text-teal-600" />
                    </div>

                    <h3 className="font-sora text-base font-semibold mt-5">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-xs leading-6 mt-2">
                      {item.description}
                    </p>

                    <div className="flex items-center gap-2 text-teal-600 text-xs font-semibold mt-4">
                      Explore
                      <ArrowRight size={13} />
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

          </div>
        </section>

        {/* =====================================================
          WORKFLOW
      ====================================================== */}
        <section className="py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                Connected Journey
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                From patient to
                <span className="text-teal-500">
                  {" "}continued care.
                </span>
              </h2>

              <p className="max-w-2xl mx-auto text-slate-600 mt-6 leading-7">
                Doxzon connects the major steps of a healthcare journey through
                one platform.
              </p>
            </div>

            <div className="relative grid md:grid-cols-5 gap-8 mt-16">

              <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-[2px] bg-slate-100" />

              {workflow.map((item) => {
                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    whileHover={{ y: -7 }}
                    className="relative text-center"
                  >
                    <div className="relative z-10 mx-auto w-16 h-16 rounded-2xl bg-slate-950 border-4 border-white shadow-lg grid place-items-center">
                      <Icon size={23} className="text-teal-400" />
                    </div>

                    <span className="block text-xs font-mono text-teal-500 tracking-widest mt-5">
                      {item.number}
                    </span>

                    <h3 className="font-sora text-lg font-bold mt-2">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-6 mt-2">
                      {item.description}
                    </p>
                  </motion.div>
                );
              })}

            </div>
          </div>
        </section>

        {/* =====================================================
          DOXZON CAPABILITIES
      ====================================================== */}
        <section className="bg-slate-950 text-white py-28">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">

            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400">
                Doxzon Capabilities
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-5">
                The building blocks
                <br />
                of connected care.
              </h2>

              <p className="text-slate-400 text-lg leading-8 mt-6 max-w-lg">
                Doxzon brings multiple healthcare workflows together so the
                patient and provider experience can remain connected.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {capabilities.map((item) => (
                <motion.div
                  key={item}
                  whileHover={{ scale: 1.02 }}
                  className="flex items-start gap-2.5 bg-white/5 border border-white/10 rounded-xl p-4"
                >
                  <div className="w-6 h-6 rounded-full bg-teal-500/15 flex items-center justify-center flex-shrink-0">
                    <Check size={13} className="text-teal-400" />
                  </div>

                  <span className="text-xs text-slate-300 leading-5">
                    {item}
                  </span>
                </motion.div>
              ))}
            </div>

          </div>
        </section>

        {/* =====================================================
          HEALTHCARE PROFESSIONALS
      ====================================================== */}
        <section className="py-24 bg-slate-50">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                For Healthcare Professionals
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Connect the tools
                <span className="text-teal-500">
                  {" "}you use every day.
                </span>
              </h2>

              <p className="text-slate-600 leading-8 mt-6">
                Bring clinical workflows such as EHR, scheduling,
                telemedicine, e-prescriptions, diagnostics and billing into a
                connected healthcare experience.
              </p>

              <button className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-950 text-white px-6 py-3.5 font-semibold">
                Explore Provider Tools
                <ArrowRight size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">

              {[
                [Stethoscope, "EHR"],
                [CalendarCheck, "Scheduling"],
                [Mic2, "Consultation"],
                [CreditCard, "Billing"],
                [Pill, "e-Prescription"],
                [FlaskConical, "Diagnostics"],
              ].map(([Icon, label]) => (
                <motion.div
                  key={label}
                  whileHover={{ y: -6 }}
                  className="bg-white border border-slate-100 rounded-xl p-4 text-center shadow-sm"
                >
                  <div className="w-9 h-9 mx-auto rounded-lg bg-teal-50 grid place-items-center">
                    <Icon size={16} className="text-teal-600" />
                  </div>

                  <p className="font-semibold text-xs mt-2">
                    {label}
                  </p>
                </motion.div>
              ))}

            </div>
          </div>
        </section>

        {/* =====================================================
          PATIENT EXPERIENCE
      ====================================================== */}
        <section className="py-24 bg-gradient-to-r from-cyan-50 to-blue-50">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                For Patients
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                One connected
                <span className="text-teal-500">
                  {" "}healthcare journey.
                </span>
              </h2>

              <p className="text-slate-600 leading-8 mt-6">
                Patients can move between finding care, booking appointments,
                consulting providers, accessing health records, completing
                diagnostics and managing prescriptions through connected
                workflows.
              </p>
            </div>

            <div className="space-y-3">

              {[
                "Find a healthcare provider",
                "Book an appointment",
                "Have a virtual or in-clinic consultation",
                "Access relevant health records",
                "Complete supported diagnostic tests",
                "Receive prescriptions and continue care",
              ].map((item) => (
                <motion.div
                  key={item}
                  whileHover={{ x: 6 }}
                  className="flex items-center gap-3 bg-white/80 border border-white rounded-lg p-3 shadow-sm"
                >
                  <span className="w-6 h-6 rounded-full bg-teal-500/10 text-teal-600 grid place-items-center flex-shrink-0">
                    <Check size={14} />
                  </span>

                  <span className="font-medium text-sm text-slate-700">
                    {item}
                  </span>
                </motion.div>
              ))}

            </div>
          </div>
        </section>

        {/* =====================================================
          SECURITY
      ====================================================== */}
        <section className="py-24">

          <div className="max-w-4xl mx-auto px-6 text-center">

            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 grid place-items-center">
              <ShieldCheck size={24} className="text-white" />
            </div>

            <span className="inline-block text-teal-500 uppercase tracking-widest font-semibold text-xs mt-6">
              Security & Privacy
            </span>

            <h2 className="font-sora text-4xl md:text-5xl font-medium mt-3">
              Connected Healthcare
              <span className="text-teal-500">
                {" "}Needs Trust.
              </span>
            </h2>

            <p className="text-slate-600 leading-8 mt-6">
              Healthcare information requires appropriate access controls,
              permissions and privacy practices. Doxzon is designed to support
              secure healthcare workflows while keeping relevant information
              connected.
            </p>

          </div>
        </section>

        {/* =====================================================
          FAQ
      ====================================================== */}
        <section className="py-24 bg-slate-50">

          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-4 mt-12">

              {[
                {
                  q: "What does Doxzon integrate?",
                  a: "Doxzon connects supported patient, clinical, diagnostic, pharmacy and healthcare information workflows through its platform.",
                },
                {
                  q: "Does Doxzon integrate with EHR?",
                  a: "Doxzon provides EHR and digital health-record capabilities. External system connectivity depends on the integrations supported by the platform.",
                },
                {
                  q: "Can diagnostic results connect with health records?",
                  a: "Where supported by the diagnostic service and available integration, relevant reports and results can be connected with the patient's healthcare information.",
                },
                {
                  q: "Does Doxzon support pharmacy workflows?",
                  a: "Doxzon supports e-Prescription and pharmacy-related workflows where those services are available and connected.",
                },
                {
                  q: "What is the eMC Card?",
                  a: "The eMC Card is a patient medical card with a QR code that can provide access to available health information according to appropriate access controls.",
                },
              ].map((faq, index) => (
                <motion.div
                  key={faq.q}
                  whileHover={{ y: -4 }}
                  className="bg-white border border-slate-100 rounded-xl p-4"
                >
                  <div className="flex gap-3">

                    <span className="w-7 h-7 flex-shrink-0 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 text-white grid place-items-center text-xs font-bold">
                      0{index + 1}
                    </span>

                    <div>
                      <h3 className="font-sora text-base font-semibold">
                        {faq.q}
                      </h3>

                      <p className="text-slate-600 text-sm leading-6 mt-2">
                        {faq.a}
                      </p>
                    </div>

                  </div>
                </motion.div>
              ))}

            </div>
          </div>
        </section>

        {/* =====================================================
          FINAL CTA
      ====================================================== */}
     <section className="px-6 pb-24">
  <div className="max-w-7xl mx-auto rounded-3xl bg-slate-950 text-white px-6 py-10 md:px-12 md:py-12 text-center">

    <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400">
      Connected Healthcare
    </span>

    <h2 className="font-sora text-3xl md:text-4xl font-medium mt-4">
      Connect Healthcare.
      <span className="text-teal-400">
        {" "}Simplify the Journey.
      </span>
    </h2>

    <p className="text-slate-300 max-w-2xl mx-auto mt-4 text-sm leading-6">
      Bring patients, healthcare professionals, diagnostics, pharmacy
      and health information together with Doxzon.
    </p>

    <Link
  to="/"
  className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-6 py-3 text-sm font-medium hover:bg-teal-50 transition"
>
  Explore Doxzon Platform
  <ArrowRight size={16} />
</Link>

  </div>
</section>

      </main>
    </>
  );
};

export default Integrations;